import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { apiUrl, supabase } from "./supabase";
import { inspectVideo, MAX_CLIP_SECONDS, MAX_UPLOAD_BYTES, prepareVideoClip } from "./videoClip";
import { AiEdgePosePreview } from "./AiEdgePosePreview";
import { AdminDashboard } from "./AdminDashboard";
import "./clip.css";
import "./report.css";
import "./performance.css";

type Athlete = { id: string; tenant_id: string; full_name: string; level: string | null; dominant_hand: string | null };
type ActionableDrill = { drill_name: string; volume: string; objective: string };
type EliteCoachReport = {
  central_diagnosis: string; main_limiter: string; weekly_priority: string;
  priority_errors: { classification: "CRITICAL" | "LIMITING" | "REFINEMENT"; symptom: string; probable_cause: string; ball_impact: string; body_impact: string; tactical_consequence: string; priority_correction: string }[];
  contact_racket_ball: string; footwork_positioning: string; tactical_analysis: string; mental_competitive_analysis: string;
  progressive_drills: { stage: string; name: string; objective: string; execution: string; volume: string; common_error: string; success_criterion: string }[];
  score_justifications: Record<string, string>;
  ball_speed_estimate: { measurable: boolean; probable_range: string; ball_type: string; basis: string; filming_improvements: string[] };
  four_week_plan: { week: number; focus: string; main_drill: string; success_metric: string }[];
  tracking_indicators: string[]; evolution_projection: string; match_checklist: string[]; head_coach_conclusion: string;
};
type AgentSuiteReport = {
  id: string; published_at: string; executive_summary: string | null; limitations: string[];
  agent_suite_version: string | null;
  athlete_metadata: { stroke_type: string; rep_count_detected: number; quality_score_overall: number } | null;
  head_coach_analysis: { tactical_summary: string; primary_technical_flaw: string; actionable_drills: ActionableDrill[] } | null;
  biomechanical_analysis: { kinetic_chain_integrity: string; energy_leak_severity: "LOW" | "MODERATE" | "CRITICAL"; knee_loading_evaluation: string; injury_risk_note: string } | null;
  nutrition_recovery_analysis: { pre_workout_protocol: string; intra_hydration_strategy: string; post_recovery_recommendation: string } | null;
  elite_coach_analysis: EliteCoachReport | null;
};
type Analysis = {
  id: string;
  tenant_id: string;
  athlete_id: string;
  status: string;
  progress: number;
  stroke_type: string;
  video_context: string;
  created_at: string;
  completed_at: string | null;
  failure_code: string | null;
  retry_count: number;
  human_review_required: boolean;
  review_status: string;
  reports: AgentSuiteReport[];
  scores: (Score & { created_at: string })[];
};
type Score = {
  technique: number | null; biomechanics: number | null; footwork: number | null;
  consistency: number | null; power: number | null; control: number | null;
  tactics: number | null; mental: number | null; conditioning: number | null;
  general_rpi: number | null; stroke_rpi: number | null; confidence: number;
  coverage_ratio: number | null; reliability: number | null; uncertainty: number | null;
};
type RpiGroupScore = { score: number; lower: number; upper: number; reliability: number; effective_sample_size: number; analysis_count: number };
type RpiSnapshot = {
  id: string; status: "insufficient" | "provisional" | "established";
  rpi: number | null; lower_bound: number | null; upper_bound: number | null;
  confidence: number; coverage: number; momentum: number | null; calculated_at: string;
  group_scores: Record<string, RpiGroupScore>; missing_requirements: string[];
};
type Finding = {
  id: string; category: string; title: string; claim: string; basis: string;
  confidence: number; severity: "low" | "medium" | "high"; limitations: string[];
  evidence: { frame_id: string; timestamp_ms: number; pose_visibility: number; metrics_2d: Record<string, number> }[];
};
type AnalysisFeedback = { rating: number; useful: boolean; comment: string | null };

type PendingAnalysisUpload = {
  uploadId: string;
  fileName: string;
  strokeType: string;
  videoContext: string;
  goal: string;
  humanReviewRequired: boolean;
  datasetUse: boolean;
};

function relationArray<T>(value: T | T[] | null | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

const scoreDimensions = [
  ["Técnica", "technique"], ["Biomecânica", "biomechanics"], ["Movimentação", "footwork"],
  ["Consistência", "consistency"], ["Potência", "power"], ["Controle", "control"],
  ["Tática", "tactics"], ["Mental", "mental"], ["Condicionamento", "conditioning"],
] as const;

function strokeLabel(stroke: string) {
  return ({ serve: "Saque", forehand: "Forehand", backhand: "Backhand", volley: "Voleio", rally: "Rally", match_point: "Ponto real" } as Record<string, string>)[stroke] ?? stroke.replace("_", " ");
}

const statusLabels: Record<string, string> = {
  created: "Na fila",
  uploaded: "Vídeo recebido",
  validating: "Validando vídeo",
  preprocessing: "Preparando evidências",
  analyzing_context: "Mapeando movimentos",
  analyzing_specialists: "Especialistas analisando",
  validating_claims: "Validando conclusões",
  awaiting_human_review: "Revisão do coach",
  rendering: "Gerando relatório",
  completed: "Concluída",
  rejected_input: "Vídeo recusado",
  needs_new_video: "Novo vídeo necessário",
  failed_retryable: "Aguardando nova tentativa",
  failed_terminal: "Falha no processamento",
  cancelled: "Cancelada",
};

function messageFrom(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.includes("monthly_analysis_quota_exceeded")) {
      return "A cota mensal deste plano foi atingida. O vídeo já está seguro e não será reenviado; tente novamente após a liberação da cota.";
    }
    return error.message;
  }
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return "Algo não saiu como esperado.";
}

async function api<T>(path: string, session: Session, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(init?.body instanceof File ? {} : { "Content-Type": "application/json" }),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(detail?.error?.message ?? `Erro HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function apiBlob(path: string, session: Session): Promise<Blob> {
  const response = await fetch(`${apiUrl}${path}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(detail?.error?.message ?? `Erro HTTP ${response.status}`);
  }
  return response.blob();
}

function Brand() {
  return (
    <div className="brand">
      <span><b>RAS SPORTS</b> Network</span>
      <small>Performance &amp; Analytics Division</small>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg className="google-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M23.49 12.27c0-.78-.07-1.53-.2-2.25H12v4.26h6.46a5.52 5.52 0 0 1-2.39 3.62v2.96h3.87c2.27-2.09 3.55-5.17 3.55-8.59Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.94-2.91l-3.87-2.96c-1.07.72-2.44 1.14-4.07 1.14-3.13 0-5.78-2.11-6.73-4.95H1.28v3.05A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.32A7.2 7.2 0 0 1 4.89 12c0-.81.14-1.59.38-2.32V6.63H1.28A12 12 0 0 0 0 12c0 1.93.46 3.76 1.28 5.37l3.99-3.05Z" />
      <path fill="#EA4335" d="M12 4.73c1.76 0 3.34.61 4.59 1.8l3.43-3.43C17.94 1.17 15.24 0 12 0A12 12 0 0 0 1.28 6.63l3.99 3.05C6.22 6.84 8.87 4.73 12 4.73Z" />
    </svg>
  );
}

function AuthScreen() {
  const phoneEnabled = import.meta.env.VITE_PHONE_AUTH_ENABLED === "true";
  const emailEnabled = import.meta.env.VITE_EMAIL_AUTH_ENABLED !== "false";
  const [phone, setPhone] = useState("+55");
  const [token, setToken] = useState("");
  const [phoneSent, setPhoneSent] = useState(false);
  const [email, setEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailMode, setEmailMode] = useState<"password" | "link" | "recovery">("password");
  const [emailSent, setEmailSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  async function sendOtp(event: FormEvent) {
    event.preventDefault();
    if (!phoneEnabled) return;
    setBusy(true); setNotice("");
    const { error } = await supabase.auth.signInWithOtp({ phone });
    setBusy(false);
    if (error) return setNotice(error.message);
    setPhoneSent(true); setNotice("Código enviado por SMS.");
  }

  async function verifyOtp(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setNotice("");
    const { error } = await supabase.auth.verifyOtp({ phone, token, type: "sms" });
    setBusy(false);
    if (error) setNotice(error.message);
  }

  async function sendEmailLink(event: FormEvent) {
    event.preventDefault();
    if (!emailEnabled) return;
    setBusy(true); setNotice("");
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/app/` },
    });
    setBusy(false);
    if (error) return setNotice(error.message);
    setEmailSent(true);
    setNotice("Link de acesso enviado por e-mail.");
  }

  async function signInWithPassword(event: FormEvent) {
    event.preventDefault();
    if (!emailEnabled) return;
    setBusy(true); setNotice("");
    const { error } = await supabase.auth.signInWithPassword({ email, password: emailPassword });
    setBusy(false);
    if (error) setNotice("E-mail ou senha inválidos. Se ainda não criou uma senha, use o link de acesso.");
  }

  async function sendPasswordRecovery(event: FormEvent) {
    event.preventDefault();
    if (!emailEnabled) return;
    setBusy(true); setNotice("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/app/`,
    });
    setBusy(false);
    if (error) return setNotice(error.message);
    setEmailSent(true);
    setNotice("Enviamos um link seguro para redefinir sua senha.");
  }

  async function signInWithGoogle() {
    setBusy(true); setNotice("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/app/`,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    setBusy(false);
    if (error) setNotice(error.message);
  }

  return (
    <main className="auth-shell">
      <section className="auth-story">
        <Brand />
        <div className="eyebrow">RAS PERFORMANCE OS</div>
        <h1>Seu jogo.<br /><em>Agora mensurável.</em></h1>
        <p>Transforme vídeo em evidência, análise técnica e um plano claro de evolução.</p>
        <div className="trust-line"><span>R2 privado</span><span>Validação automática</span><span>LGPD by design</span></div>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <span className="step-tag">ÁREA DO ATLETA</span>
          <h2>Acesse seu Performance Center</h2>
          <p>Entre com Google, e-mail/senha ou use um link seguro no primeiro acesso.</p>
          <div className="auth-methods">
            <section className="auth-method">
              <div className="method-heading">
                <span className="method-icon google-icon"><GoogleMark /></span>
                <div><strong>Google</strong><small>Rápido e seguro</small></div>
              </div>
              <button className="google-auth-button" type="button" onClick={signInWithGoogle} disabled={busy}>
                <GoogleMark />
                <span>{busy ? "Redirecionando…" : "Continuar com Google"}</span>
              </button>
            </section>
            <section className={`auth-method ${phoneEnabled ? "" : "is-unavailable"}`} aria-disabled={!phoneEnabled}>
              <div className="method-heading">
                <span className="method-icon">SMS</span>
                <div><strong>Celular</strong><small>{phoneEnabled ? "Disponível" : "Em configuração"}</small></div>
              </div>
              {!phoneEnabled ? (
                <p className="method-status">A autenticação por SMS será habilitada após a conexão segura com a D7 Networks.</p>
              ) : !phoneSent ? (
                <form onSubmit={sendOtp}>
                  <label>Celular com DDI</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" placeholder="+55 11 99999-9999" required />
                  <button disabled={busy}>{busy ? "Enviando…" : "Receber código"}</button>
                </form>
              ) : (
                <form onSubmit={verifyOtp}>
                  <p className="method-status">Enviamos um código para {phone}.</p>
                  <label>Código de 6 dígitos</label>
                  <input value={token} onChange={(e) => setToken(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" required />
                  <button disabled={busy || token.length !== 6}>{busy ? "Validando…" : "Validar e entrar"}</button>
                  <button className="button-link" type="button" onClick={() => { setPhoneSent(false); setToken(""); }}>Alterar número</button>
                </form>
              )}
            </section>
            <section className={`auth-method ${emailEnabled ? "" : "is-unavailable"}`} aria-disabled={!emailEnabled}>
              <div className="method-heading">
                <span className="method-icon">@</span>
                <div><strong>E-mail</strong><small>{emailEnabled ? "Disponível" : "Indisponível"}</small></div>
              </div>
              {!emailEnabled ? (
                <p className="method-status">Este método está temporariamente indisponível.</p>
              ) : emailSent ? (
                <div className="email-sent"><strong>Verifique sua caixa de entrada.</strong><p>Enviamos um link seguro para {email}.</p><button className="button-link" type="button" onClick={() => setEmailSent(false)}>Usar outro e-mail</button></div>
              ) : emailMode === "password" ? (
                <form onSubmit={signInWithPassword}>
                  <label>Seu e-mail</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" placeholder="atleta@exemplo.com" required />
                  <label>Senha</label>
                  <input value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} type="password" autoComplete="current-password" minLength={10} required />
                  <button disabled={busy}>{busy ? "Entrando…" : "Entrar com e-mail e senha"}</button>
                  <button className="button-link" type="button" onClick={() => { setEmailMode("link"); setNotice(""); }}>Primeiro acesso sem senha</button>
                  <button className="button-link" type="button" onClick={() => { setEmailMode("recovery"); setNotice(""); }}>Esqueci minha senha</button>
                </form>
              ) : emailMode === "link" ? (
                <form onSubmit={sendEmailLink}>
                  <label>Seu e-mail</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email" autoComplete="email" placeholder="atleta@exemplo.com" required />
                  <button disabled={busy}>{busy ? "Enviando…" : "Receber link de acesso"}</button>
                  <button className="button-link" type="button" onClick={() => { setEmailMode("password"); setNotice(""); }}>Voltar para login com senha</button>
                </form>
              ) : (
                <form onSubmit={sendPasswordRecovery}>
                  <label>E-mail da conta</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email" autoComplete="email" placeholder="atleta@exemplo.com" required />
                  <button disabled={busy}>{busy ? "Enviando…" : "Redefinir minha senha"}</button>
                  <button className="button-link" type="button" onClick={() => { setEmailMode("password"); setNotice(""); }}>Voltar para login com senha</button>
                </form>
              )}
            </section>
          </div>
          {notice && <div className="notice">{notice}</div>}
          <small className="legal">Ao continuar, você concorda com os termos e a política de privacidade vigentes.</small>
        </div>
      </section>
    </main>
  );
}

function PasswordSetup({ email, onDone }: { email: string; onDone: () => Promise<void> }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) return setError("As senhas precisam ser iguais.");
    setBusy(true); setError("");
    const { error: updateError } = await supabase.auth.updateUser({
      password,
      data: { password_set: true },
    });
    if (updateError) {
      setError(updateError.message);
      setBusy(false);
      return;
    }
    await onDone();
    setBusy(false);
  }

  return (
    <div className="onboarding-shell">
      <Brand />
      <form className="onboarding-card" onSubmit={submit}>
        <div className="progress-dots"><i className="active" /><i /><i /></div>
        <span className="eyebrow">E-MAIL CONFIRMADO</span>
        <h1>Crie sua senha de acesso.</h1>
        <p>Seu login será <strong>{email}</strong>. O link por e-mail continuará disponível para recuperação.</p>
        <div className="field-grid">
          <label className="wide">Nova senha<input type="password" minLength={10} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required /><small>Mínimo de 10 caracteres.</small></label>
          <label className="wide">Confirmar senha<input type="password" minLength={10} autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required /></label>
        </div>
        <button disabled={busy || password.length < 10}>{busy ? "Salvando…" : "Criar senha e continuar"}</button>
        {error && <div className="error-box">{error}</div>}
      </form>
    </div>
  );
}

function Onboarding({ session, onDone }: { session: Session; onDone: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ fullName: "", level: "intermediate", dominantHand: "right", goal: "" });

  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const { error: profileError } = await supabase.rpc("create_athlete_profile", {
        p_full_name: form.fullName,
        p_level: form.level,
        p_dominant_hand: form.dominantHand,
        p_goals: form.goal,
      });
      if (profileError) throw profileError;
      await onDone();
    } catch (caught) { setError(messageFrom(caught)); } finally { setBusy(false); }
  }

  return (
    <div className="onboarding-shell">
      <Brand />
      <form className="onboarding-card" onSubmit={submit}>
        <div className="progress-dots"><i className="active" /><i /><i /></div>
        <span className="eyebrow">PRIMEIRO ACESSO</span>
        <h1>Vamos criar seu perfil de performance.</h1>
        <p>Esses dados contextualizam a análise. Você poderá completar medidas e histórico depois.</p>
        <div className="field-grid">
          <label className="wide">Nome completo<input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></label>
          <label>Nível<select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}><option value="beginner">Iniciante</option><option value="intermediate">Intermediário</option><option value="advanced">Avançado</option><option value="competitive">Competitivo</option><option value="professional">Profissional</option></select></label>
          <label>Mão dominante<select value={form.dominantHand} onChange={(e) => setForm({ ...form, dominantHand: e.target.value })}><option value="right">Direita</option><option value="left">Esquerda</option><option value="ambidextrous">Ambidestro</option></select></label>
          <label className="wide">Objetivo principal<textarea required value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Ex.: ganhar consistência no segundo saque" /></label>
        </div>
        <button disabled={busy}>{busy ? "Criando perfil…" : "Abrir meu dashboard"}</button>
        {error && <div className="error-box">{error}</div>}
      </form>
    </div>
  );
}

function UploadPanel({ athlete, session, onCreated }: { athlete: Athlete; session: Session; onCreated: () => Promise<void> }) {
  const pendingStorageKey = `ras:pending-analysis:${session.user.id}:${athlete.id}`;
  const [pendingUpload, setPendingUpload] = useState<PendingAnalysisUpload | null>(() => {
    try {
      const stored = window.localStorage.getItem(pendingStorageKey);
      return stored ? JSON.parse(stored) as PendingAnalysisUpload : null;
    } catch { return null; }
  });
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState(0);
  const [clipStart, setClipStart] = useState(0);
  const [clipEnd, setClipEnd] = useState(0);
  const [strokeType, setStrokeType] = useState(pendingUpload?.strokeType ?? "serve");
  const [context, setContext] = useState(pendingUpload?.videoContext ?? "training");
  const [goal, setGoal] = useState(pendingUpload?.goal ?? "");
  const [datasetUse, setDatasetUse] = useState(pendingUpload?.datasetUse ?? false);
  const [humanReviewRequired, setHumanReviewRequired] = useState(pendingUpload?.humanReviewRequired ?? false);
  const [consent, setConsent] = useState(Boolean(pendingUpload));
  const [inspecting, setInspecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(pendingUpload ? 90 : 0);
  const [notice, setNotice] = useState(pendingUpload ? "Upload concluído. Retome a análise sem reenviar o vídeo, mesmo após fechar esta aba." : "");
  const [noticeKind, setNoticeKind] = useState<"info" | "success" | "error">("info");
  const videoRef = useRef<HTMLVideoElement>(null);
  const selectionSequence = useRef(0);
  const submittingRef = useRef(false);
  const previewUrl = useMemo(() => file ? URL.createObjectURL(file) : "", [file]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  async function selectFile(selected: File | null) {
    const sequence = ++selectionSequence.current;
    if (selected && pendingUpload) {
      setInspecting(true);
      setNoticeKind("info");
      setNotice("Liberando o envio anterior antes de trocar o vídeo…");
      try {
        await api(`/v1/uploads/${pendingUpload.uploadId}`, session, { method: "DELETE" });
      } catch (caught) {
        if (sequence === selectionSequence.current) {
          setNoticeKind("error");
          setNotice(messageFrom(caught));
          setInspecting(false);
        }
        return;
      }
      if (sequence !== selectionSequence.current) return;
    }
    window.localStorage.removeItem(pendingStorageKey);
    setPendingUpload(null);
    setFile(selected); setDuration(0); setClipStart(0); setClipEnd(0); setProgress(0); setNotice(""); setNoticeKind("info");
    if (!selected) { setInspecting(false); return; }
    setInspecting(true); setNotice("Arquivo selecionado. Lendo os metadados do vídeo…");
    try {
      const inspectedDuration = await inspectVideo(selected);
      if (sequence !== selectionSequence.current) return;
      setFile(selected);
      setDuration(inspectedDuration);
      setClipEnd(Math.min(inspectedDuration, MAX_CLIP_SECONDS));
      setNotice(inspectedDuration > MAX_CLIP_SECONDS
        ? "Escolha abaixo o trecho de até 60 segundos. O original não será enviado."
        : "Vídeo pronto. Você pode ajustar o trecho antes de enviar.");
    } catch (caught) {
      if (sequence !== selectionSequence.current) return;
      setNotice(messageFrom(caught));
    } finally {
      if (sequence === selectionSequence.current) setInspecting(false);
    }
  }

  async function discardVideo() {
    if (busy) return;
    const upload = pendingUpload;
    setBusy(true);
    setNoticeKind("info");
    setNotice(upload ? "Cancelando este envio e removendo o vídeo…" : "");
    try {
      if (upload) await api(`/v1/uploads/${upload.uploadId}`, session, { method: "DELETE" });
      window.localStorage.removeItem(pendingStorageKey);
      setPendingUpload(null); setFile(null); setDuration(0); setClipStart(0); setClipEnd(0); setProgress(0); setNotice("");
    } catch (caught) {
      setNoticeKind("error");
      setNotice(messageFrom(caught));
    } finally {
      setBusy(false);
    }
  }

  function mimeTypeFor(selected: File): string {
    if (["video/mp4", "video/quicktime", "video/webm"].includes(selected.type)) return selected.type;
    if (/\.mov$/i.test(selected.name)) return "video/quicktime";
    if (/\.webm$/i.test(selected.name)) return "video/webm";
    return "video/mp4";
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submittingRef.current || !consent || (!pendingUpload && (!file || !duration))) return;
    submittingRef.current = true;
    setBusy(true);
    setNoticeKind("info");
    let uploadForAnalysis = pendingUpload;
    try {
      if (!uploadForAnalysis) {
        if (!file) return;
        setProgress(4); setNotice("Preparando o trecho no seu dispositivo…");
        const prepared = await prepareVideoClip(file, {
          durationSeconds: duration,
          startSeconds: clipStart,
          endSeconds: clipEnd,
        }, (clipProgress) => setProgress(4 + Math.round(clipProgress * 30)));
        if (prepared.size > MAX_UPLOAD_BYTES) throw new Error("O trecho final excede 100 MiB. Selecione um intervalo menor.");
        const mimeType = mimeTypeFor(prepared);

        setProgress(38); setNotice("Autorizando upload direto…");
        const created = await api<{ uploadId: string; uploadUrl: string; uploadHeaders: Record<string, string>; completeUrl: string }>("/v1/uploads", session, {
          method: "POST", body: JSON.stringify({
            athleteId: athlete.id,
            fileName: prepared.name,
            mimeType,
            sizeBytes: prepared.size,
            sourceDurationSeconds: duration,
            clipStartSeconds: clipStart,
            clipEndSeconds: clipEnd,
            clientTrimmed: prepared !== file,
          }),
        });
        setProgress(48); setNotice("Enviando somente o trecho para o R2 privado…");
        const uploadResponse = await fetch(created.uploadUrl, { method: "PUT", body: prepared, headers: created.uploadHeaders });
        if (!uploadResponse.ok) throw new Error(`O R2 recusou o upload (HTTP ${uploadResponse.status}).`);

        setProgress(82); setNotice("Confirmando o arquivo no R2…");
        await api(created.completeUrl, session, { method: "POST", body: JSON.stringify({}) });
        uploadForAnalysis = { uploadId: created.uploadId, fileName: prepared.name, strokeType, videoContext: context, goal, humanReviewRequired, datasetUse };
        window.localStorage.setItem(pendingStorageKey, JSON.stringify(uploadForAnalysis));
        setPendingUpload(uploadForAnalysis);
      }
      setProgress(90); setNotice("Criando análise…");
      await api("/v1/analyses", session, {
        method: "POST",
        headers: { "Idempotency-Key": uploadForAnalysis.uploadId },
        body: JSON.stringify({
          uploadId: uploadForAnalysis.uploadId, athleteId: athlete.id, strokeType, videoContext: context, goal, humanReviewRequired,
          painOrInjuryNotes: null,
          consent: { analysis: true, privacyPolicyVersion: "2026-08-10", datasetUse, legalGuardianName: null },
        }),
      });
      window.localStorage.removeItem(pendingStorageKey);
      setPendingUpload(null);
      setProgress(100); setNotice("Tudo certo! Vídeo recebido e análise enviada para processamento."); setNoticeKind("success");
      setFile(null); setDuration(0); setClipStart(0); setClipEnd(0); setGoal("");
      await onCreated();
    } catch (caught) {
      setNotice(messageFrom(caught));
      setNoticeKind("error");
      setProgress(uploadForAnalysis ? 90 : 0);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  }

  return (
    <form className="upload-card" onSubmit={submit}>
      <div className="card-heading"><div><span className="eyebrow">NOVA ANÁLISE</span><h2>Envie um movimento</h2></div><span className="quota">até 60s · 100 MiB</span></div>
      <label className={`dropzone ${file ? "has-file" : ""}`}>
        <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" onChange={(e) => { const selected = e.currentTarget.files?.[0] ?? null; e.currentTarget.value = ""; void selectFile(selected); }} disabled={busy} />
        <span className="upload-icon">↑</span>
        <strong>{file ? file.name : "Selecione seu vídeo"}</strong>
        <small>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MiB` : "MP4, MOV ou WebM · corpo inteiro visível"}</small>
      </label>
      {file && <section className="clip-editor">
        <button className="remove-video" type="button" aria-label="Remover este vídeo" title="Remover este vídeo" disabled={busy} onClick={() => void discardVideo()}>×</button>
        <AiEdgePosePreview src={previewUrl} videoRef={videoRef} enabled={consent} />
        {duration > 0 ? <>
        <div className="clip-summary"><strong>Trecho selecionado</strong><span>{clipStart.toFixed(1)}s — {clipEnd.toFixed(1)}s · {(clipEnd - clipStart).toFixed(1)}s</span></div>
        <label>Início
          <input type="range" min="0" max={Math.max(0, duration - 1)} step="0.1" value={clipStart} onChange={(e) => {
            const next = Number(e.target.value);
            setClipStart(next); setClipEnd(Math.min(duration, Math.max(next + 1, Math.min(clipEnd, next + MAX_CLIP_SECONDS))));
            if (videoRef.current) videoRef.current.currentTime = next;
          }} />
        </label>
        <label>Fim
          <input type="range" min={Math.min(duration, clipStart + 1)} max={Math.min(duration, clipStart + MAX_CLIP_SECONDS)} step="0.1" value={clipEnd} onChange={(e) => setClipEnd(Number(e.target.value))} />
        </label>
        <small>O corte acontece neste dispositivo. Apenas este trecho será enviado ao Cloudflare R2.</small>
        </> : <div className={`clip-inspection ${inspecting ? "loading" : "error"}`} role="status" aria-live="polite">{inspecting ? "Lendo duração e preparando o preview…" : notice}</div>}
      </section>}
      <div className="field-grid compact">
        <label>Golpe<select value={strokeType} onChange={(e) => setStrokeType(e.target.value)}><option value="serve">Saque</option><option value="forehand">Forehand</option><option value="backhand">Backhand</option><option value="volley">Voleio</option><option value="rally">Rally</option><option value="match_point">Ponto real</option></select></label>
        <label>Contexto<select value={context} onChange={(e) => setContext(e.target.value)}><option value="training">Treino</option><option value="drill">Drill</option><option value="rally">Rally</option><option value="match">Jogo</option></select></label>
        <label>Revisão humana<select value={humanReviewRequired ? "yes" : "no"} onChange={(e) => setHumanReviewRequired(e.target.value === "yes")}><option value="no">Não — publicação automática</option><option value="yes">Sim — revisar antes de publicar</option></select></label>
        <label className="wide">O que você quer melhorar?<textarea required minLength={3} maxLength={500} value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Ex.: aumentar consistência sem perder potência" /></label>
      </div>
      <label className="check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> Autorizo o processamento deste vídeo para gerar minha análise.</label>
      <label className="check subtle"><input type="checkbox" checked={datasetUse} onChange={(e) => setDatasetUse(e.target.checked)} /> Autorizo separadamente o uso anonimizado para melhorar o dataset RAS (opcional).</label>
      {progress > 0 && <div className="upload-progress"><i style={{ width: `${progress}%` }} /></div>}
      {notice && <p className={`status-note ${noticeKind}`} role="status" aria-live="polite">{notice}</p>}
      <button disabled={busy || inspecting || (!pendingUpload && (!file || !duration)) || !consent}>{busy ? "Processando envio…" : inspecting ? "Lendo vídeo…" : pendingUpload ? "Retomar envio para análise" : "Cortar e iniciar análise"}</button>
    </form>
  );
}

function PerformanceOverview({ analyses, rpiSnapshots }: { analyses: Analysis[]; rpiSnapshots: RpiSnapshot[] }) {
  const published = analyses
    .filter((analysis) => analysis.reports?.some((report) => report.published_at) && analysis.scores?.[0])
    .sort((a, b) => new Date(a.completed_at ?? a.created_at).getTime() - new Date(b.completed_at ?? b.created_at).getTime());
  const latest = published.at(-1)?.scores[0] ?? null;
  const globalHistory = rpiSnapshots.filter((snapshot) => snapshot.rpi != null).slice().reverse();
  const globalLatest = rpiSnapshots[0] ?? null;
  const globalPrevious = globalHistory.at(-2) ?? null;
  const delta = globalLatest?.rpi != null && globalPrevious?.rpi != null ? globalLatest.rpi - globalPrevious.rpi : null;
  const chartPoints = globalHistory.map((snapshot, index) => {
    const score = snapshot.rpi ?? 0;
    const x = globalHistory.length === 1 ? 50 : 5 + (index / (globalHistory.length - 1)) * 90;
    const y = 92 - score * 8.2;
    return `${x},${y}`;
  }).join(" ");
  const ranked = latest ? scoreDimensions
    .flatMap(([label, key]) => latest[key] == null ? [] : [{ label, value: latest[key] as number }])
    .sort((a, b) => b.value - a.value) : [];
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];
  const strokes = ["serve", "forehand", "backhand", "volley"].map((stroke) => {
    const items = published.filter((analysis) => analysis.stroke_type === stroke);
    return { stroke, count: items.length, score: items.at(-1)?.scores[0]?.stroke_rpi ?? null };
  });
  const missingLabels = (globalLatest?.missing_requirements ?? ["serve", "forehand", "backhand", "movement"])
    .map((item) => item === "movement" ? "Movimentação" : strokeLabel(item));

  return (
    <section className="performance-overview">
      <div className="section-title"><div><span className="eyebrow">EVOLUÇÃO DO ATLETA</span><h2>Performance em perspectiva</h2></div><span className="data-badge">{published.length} {published.length === 1 ? "marco publicado" : "marcos publicados"}</span></div>
      <div className="performance-grid">
        <article className="evolution-card">
          <div className="insight-head"><div><small>RPI Global</small><strong>{globalLatest?.rpi?.toFixed(2) ?? "Incompleto"}</strong></div>{globalLatest?.momentum != null ? <span className={globalLatest.momentum >= 0 ? "positive" : "negative"}>{globalLatest.momentum >= 0 ? "+" : ""}{globalLatest.momentum.toFixed(2)} / 30d</span> : delta != null && <span className={delta >= 0 ? "positive" : "negative"}>{delta >= 0 ? "+" : ""}{delta.toFixed(2)}</span>}</div>
          {globalLatest?.rpi != null && <p className="rpi-status">{globalLatest.status === "established" ? "Estabelecido" : "Provisório"} · IC {globalLatest.lower_bound?.toFixed(2)}–{globalLatest.upper_bound?.toFixed(2)} · cobertura {Math.round(globalLatest.coverage * 100)}%</p>}
          {globalLatest?.rpi != null && globalLatest.momentum == null && <p className="momentum-pending">Momentum RPI será liberado com quatro semanas comparáveis de acompanhamento.</p>}
          {globalHistory.length ? <div className="evolution-chart"><svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Evolução do RAS Performance Index"><defs><linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#168cf1" stopOpacity=".3"/><stop offset="1" stopColor="#168cf1" stopOpacity="0"/></linearGradient></defs><polyline className="chart-area" points={`5,96 ${chartPoints} 95,96`} /><polyline className="chart-line" points={chartPoints} /></svg><div className="chart-axis"><span>Primeiro protocolo</span><span>Mais recente</span></div></div> : <div className="overview-empty">Complete o protocolo com {missingLabels.join(", ")} para liberar o RPI Global.</div>}
        </article>
        <article className="dimension-card">
          <small>Mapa atual</small><h3>Dimensões de performance</h3>
          {ranked.length ? <div className="dashboard-bars">{ranked.slice(0, 6).map((item) => <div key={item.label}><span>{item.label}</span><strong>{item.value.toFixed(1)}</strong><i><b style={{ width: `${item.value * 10}%` }} /></i></div>)}</div> : <div className="overview-empty compact-empty">Disponível após uma análise válida.</div>}
        </article>
        <article className="priority-card">
          <small>Leitura rápida</small><h3>Foco de evolução</h3>
          {strongest && weakest ? <><div className="focus-item strong"><span>Ponto forte atual</span><strong>{strongest.label}</strong><b>{strongest.value.toFixed(1)}</b></div><div className="focus-item"><span>Maior oportunidade</span><strong>{weakest.label}</strong><b>{weakest.value.toFixed(1)}</b></div><p>Use os achados do relatório para transformar a nota em uma ação de treino.</p></> : <div className="overview-empty compact-empty">O foco será calculado sem inventar dimensões não mensuráveis.</div>}
        </article>
      </div>
      <div className="stroke-panel"><div><span className="eyebrow">BIBLIOTECA DE GOLPES</span><h3>Cobertura técnica</h3></div><div className="stroke-grid">{strokes.map((item) => <article key={item.stroke}><span className="stroke-monogram">{strokeLabel(item.stroke).slice(0, 2)}</span><div><strong>{strokeLabel(item.stroke)}</strong><small>{item.count ? `${item.count} ${item.count === 1 ? "análise" : "análises"}` : "Ainda não analisado"}</small></div><b>{item.score?.toFixed(1) ?? "—"}</b></article>)}</div></div>
    </section>
  );
}

function ReportViewer({ analysis, session, onClose }: { analysis: Analysis; session: Session; onClose: () => void }) {
  const report = analysis.reports.find((item) => item.published_at) ?? null;
  const elite = report?.elite_coach_analysis ?? null;
  const [score, setScore] = useState<Score | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [artifactUrl, setArtifactUrl] = useState("");
  const [artifactStatus, setArtifactStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const [reportPdfUrl, setReportPdfUrl] = useState("");
  const [reportPdfStatus, setReportPdfStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const [feedback, setFeedback] = useState<AnalysisFeedback>({ rating: 0, useful: true, comment: "" });
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackNotice, setFeedbackNotice] = useState("");

  useEffect(() => {
    let active = true;
    void Promise.all([
      supabase.from("scores").select("technique,biomechanics,footwork,consistency,power,control,tactics,mental,conditioning,general_rpi,stroke_rpi,confidence,coverage_ratio,reliability,uncertainty").eq("analysis_id", analysis.id).maybeSingle(),
      supabase.from("findings").select("id,category,title,claim,basis,confidence,severity,limitations,evidence").eq("analysis_id", analysis.id).order("severity", { ascending: false }),
    ]).then(([scoreResult, findingResult]) => {
      if (!active) return;
      const queryError = scoreResult.error ?? findingResult.error;
      if (queryError) setError(queryError.message);
      else {
        setScore(scoreResult.data as Score | null);
        setFindings((findingResult.data ?? []) as Finding[]);
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, [analysis.id]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    void apiBlob(`/v1/analyses/${analysis.id}/artifacts/annotated-video/content`, session)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setArtifactUrl(objectUrl);
        setArtifactStatus("ready");
      })
      .catch(() => { if (active) setArtifactStatus("unavailable"); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [analysis.id, session]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    void apiBlob(`/v1/analyses/${analysis.id}/artifacts/report-pdf/content`, session)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setReportPdfUrl(objectUrl);
        setReportPdfStatus("ready");
      })
      .catch(() => { if (active) setReportPdfStatus("unavailable"); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [analysis.id, session]);

  useEffect(() => {
    let active = true;
    void supabase.from("analysis_feedback").select("rating,useful,comment").eq("analysis_id", analysis.id).maybeSingle()
      .then(({ data, error: feedbackError }) => {
        if (!active || feedbackError || !data) return;
        setFeedback({ rating: data.rating, useful: data.useful, comment: data.comment ?? "" });
        setFeedbackNotice("Avaliação registrada.");
      });
    return () => { active = false; };
  }, [analysis.id]);

  const dimensions = score ? scoreDimensions.slice(0, 6).map(([label, key]) => [label, score[key]] as const) : [];
  const artifactFilename = `ras-sports-${analysis.stroke_type}-${new Date(analysis.completed_at ?? analysis.created_at).toISOString().slice(0, 10)}-overlay.mp4`;
  const reportPdfFilename = `ras-sports-${analysis.stroke_type}-${new Date(analysis.completed_at ?? analysis.created_at).toISOString().slice(0, 10)}-relatorio.pdf`;

  function printReport() {
    const previousTitle = document.title;
    document.title = `RAS SPORTS - ${strokeLabel(analysis.stroke_type)} - ${new Date(analysis.completed_at ?? analysis.created_at).toISOString().slice(0, 10)}`;
    window.print();
    window.setTimeout(() => { document.title = previousTitle; }, 500);
  }

  async function submitFeedback(event: FormEvent) {
    event.preventDefault();
    if (!feedback.rating) return setFeedbackNotice("Selecione uma nota de 1 a 5.");
    const normalizedComment = feedback.comment?.trim() ?? "";
    if (normalizedComment.length > 0 && normalizedComment.length < 3) return setFeedbackNotice("O comentário precisa ter ao menos 3 caracteres.");
    setFeedbackBusy(true);
    setFeedbackNotice("");
    const { error: feedbackError } = await supabase.from("analysis_feedback").upsert({
      analysis_id: analysis.id,
      tenant_id: analysis.tenant_id,
      athlete_id: analysis.athlete_id,
      user_id: session.user.id,
      rating: feedback.rating,
      useful: feedback.useful,
      comment: normalizedComment || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "analysis_id" });
    setFeedbackBusy(false);
    setFeedbackNotice(feedbackError ? "Não foi possível salvar agora. Tente novamente." : "Obrigado. Sua avaliação foi registrada.");
  }

  return (
    <section className="report-view">
      <div className="print-brand"><img src="/assets/Logo_Performance.jpg" alt="RAS SPORTS Network - Performance & Analytics Division" /><span>ELITE PERFORMANCE REPORT</span></div>
      <header className="report-hero">
        <div><span className="eyebrow">RELATÓRIO PUBLICADO</span><h2>{strokeLabel(analysis.stroke_type)}</h2><p>{new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(new Date(analysis.completed_at ?? analysis.created_at))} · {analysis.human_review_required ? "revisado por especialista" : "publicado automaticamente"}</p></div>
        <div className="report-actions">
          {reportPdfStatus === "ready" ? <a className="view-report-pdf" href={reportPdfUrl} target="_blank" rel="noreferrer">Visualizar documento <span>PDF</span></a> : <span className="report-pdf-pending">{reportPdfStatus === "loading" ? "Preparando documento…" : "PDF em preparação"}</span>}
          {reportPdfStatus === "ready" && <a className="download-report-pdf" href={reportPdfUrl} download={reportPdfFilename}>Baixar PDF</a>}
          <button className="print-report" type="button" onClick={printReport}>Imprimir</button>
          <button className="ghost" onClick={onClose}>Voltar ao dashboard</button>
        </div>
      </header>
      {loading ? <div className="report-loading">Carregando evidências e indicadores…</div> : error ? <div className="error-box">{error}</div> : <>
        {report?.executive_summary && <div className="executive-summary"><span className="eyebrow">RESUMO EXECUTIVO</span><p>{report.executive_summary}</p></div>}
        <section className="annotated-video-card">
          <div><span className="eyebrow">EVIDÊNCIA VISUAL</span><h3>Movimento mapeado por YOLO + MediaPipe</h3><p>Trecho curto de {strokeLabel(analysis.stroke_type).toLowerCase()} com o esqueleto biomecânico sobre o movimento real do atleta.</p></div>
          {artifactStatus === "ready" ? <div className="artifact-media"><video src={artifactUrl} controls playsInline preload="metadata" /><a className="artifact-download" href={artifactUrl} download={artifactFilename}>Baixar vídeo com overlay <span>MP4</span></a></div> : artifactStatus === "loading" ? <div className="artifact-placeholder">Carregando vídeo analisado…</div> : <div className="artifact-placeholder">A sobreposição visual ainda está sendo preparada.</div>}
        </section>
        <div className="report-score-grid">
          <article className="rpi-score"><small>RPI do golpe</small><strong>{score?.stroke_rpi?.toFixed(2) ?? "—"}</strong><span>confiabilidade {score?.reliability != null ? Math.round(score.reliability * 100) : 0}%{score?.uncertainty != null ? ` · ±${score.uncertainty.toFixed(2)}` : ""}</span></article>
          <div className="dimension-scores">{dimensions.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value?.toFixed(1) ?? "—"}</strong><i><b style={{ width: `${(value ?? 0) * 10}%` }} /></i></div>)}</div>
        </div>
        <div className="report-section-heading"><div><span className="eyebrow">DIAGNÓSTICO TÉCNICO</span><h3>Achados validados</h3></div><span className="reviewed-badge">{analysis.human_review_required ? "revisão humana" : "validação automática"}</span></div>
        {!findings.length ? <div className="report-empty">O relatório foi publicado sem achados técnicos detalhados.</div> : <div className="finding-list">{findings.map((finding) => <article key={finding.id} className={`finding ${finding.severity}`}><div className="finding-top"><span>{finding.category}</span><b>{finding.severity === "high" ? "Prioridade alta" : finding.severity === "medium" ? "Prioridade média" : "Observação"}</b></div><h4>{finding.title}</h4><p>{finding.claim}</p>{finding.evidence?.length > 0 && <div className="evidence-refs" aria-label="Evidências do achado">{finding.evidence.map((frame) => <span key={frame.frame_id}>Frame {(frame.timestamp_ms / 1000).toFixed(2)}s · pose {Math.round(frame.pose_visibility * 100)}%</span>)}</div>}<footer><span>Base: {finding.basis === "direct_visual" ? "evidência visual" : finding.basis === "calculated" ? "cálculo" : "inferência"}</span><span>Confiança {Math.round(finding.confidence * 100)}%</span></footer>{finding.limitations.length > 0 && <small>Limitação: {finding.limitations.join(" · ")}</small>}</article>)}</div>}
        {report?.head_coach_analysis && <section className="agent-suite-section"><div className="report-section-heading"><div><span className="eyebrow">HEAD COACH</span><h3>Plano técnico de evolução</h3></div><span className="agent-version">{report.agent_suite_version}</span></div><div className="agent-summary-grid"><article><small>Leitura tático-técnica</small><p>{report.head_coach_analysis.tactical_summary}</p></article><article className="priority-agent-card"><small>Falha técnica prioritária</small><p>{report.head_coach_analysis.primary_technical_flaw}</p></article></div><div className="drill-grid">{report.head_coach_analysis.actionable_drills.map((drill) => <article key={`${drill.drill_name}-${drill.volume}`}><span>DRILL</span><h4>{drill.drill_name}</h4><b>{drill.volume}</b><p>{drill.objective}</p></article>)}</div></section>}
        {elite && <>
          <section className="elite-diagnosis"><span className="eyebrow">DIAGNÓSTICO CENTRAL</span><h3>{elite.main_limiter}</h3><p>{elite.central_diagnosis}</p><strong>Prioridade da semana: {elite.weekly_priority}</strong></section>
          <section className="agent-suite-section"><div className="report-section-heading"><div><span className="eyebrow">ERROS PRIORITÁRIOS</span><h3>O que mais limita a evolução</h3></div></div><div className="priority-error-grid">{elite.priority_errors.map((item, index) => <article key={`${item.classification}-${index}`} className={item.classification.toLowerCase()}><span>{item.classification === "CRITICAL" ? "Crítico" : item.classification === "LIMITING" ? "Limitante" : "Refinamento"}</span><h4>{item.symptom}</h4><p><b>Causa provável:</b> {item.probable_cause}</p><p><b>Impacto na bola:</b> {item.ball_impact}</p><p><b>Impacto no corpo:</b> {item.body_impact}</p><p><b>Correção:</b> {item.priority_correction}</p></article>)}</div></section>
          <section className="agent-suite-section"><div className="report-section-heading"><div><span className="eyebrow">LEITURA COMPLETA</span><h3>Contato, movimentação e tomada de decisão</h3></div></div><div className="elite-reading-grid"><article><small>Contato, raquete e bola</small><p>{elite.contact_racket_ball}</p></article><article><small>Footwork e posicionamento</small><p>{elite.footwork_positioning}</p></article><article><small>Análise tática</small><p>{elite.tactical_analysis}</p></article><article><small>Mental e competitivo</small><p>{elite.mental_competitive_analysis}</p></article></div></section>
          <section className="agent-suite-section"><div className="report-section-heading"><div><span className="eyebrow">DRILLS PROGRESSIVOS</span><h3>Da técnica à pressão</h3></div></div><div className="progressive-drill-grid">{elite.progressive_drills.map((drill, index) => <article key={`${drill.stage}-${drill.name}`}><span>ETAPA {index + 1}</span><h4>{drill.name}</h4><b>{drill.volume}</b><p>{drill.execution}</p><small>Critério de sucesso</small><p>{drill.success_criterion}</p></article>)}</div></section>
          <section className="agent-suite-section"><div className="report-section-heading"><div><span className="eyebrow">PLANO DE EVOLUÇÃO</span><h3>Próximas quatro semanas</h3></div></div><div className="four-week-grid">{elite.four_week_plan.map((week) => <article key={week.week}><span>SEMANA {week.week}</span><h4>{week.focus}</h4><p>{week.main_drill}</p><small>{week.success_metric}</small></article>)}</div><div className="tracking-block"><div><small>Projeção responsável</small><p>{elite.evolution_projection}</p></div><div><small>Indicadores de tracking</small><p>{elite.tracking_indicators.join(" · ")}</p></div></div></section>
          <section className="match-checklist"><div><span className="eyebrow">CHECKLIST DE JOGO</span><h3>Cinco comandos para a quadra</h3></div><ol>{elite.match_checklist.map((item) => <li key={item}>{item}</li>)}</ol><p>{elite.head_coach_conclusion}</p></section>
        </>}
        {report?.biomechanical_analysis && <section className="agent-suite-section"><div className="report-section-heading"><div><span className="eyebrow">BIOMECÂNICA</span><h3>Cadeia cinética e carga articular</h3></div><span className={`severity-pill ${report.biomechanical_analysis.energy_leak_severity.toLowerCase()}`}>Vazamento {report.biomechanical_analysis.energy_leak_severity}</span></div><div className="agent-detail-grid"><article><small>Integridade da cadeia</small><p>{report.biomechanical_analysis.kinetic_chain_integrity}</p></article><article><small>Carga de joelhos</small><p>{report.biomechanical_analysis.knee_loading_evaluation}</p></article><article><small>Nota preventiva</small><p>{report.biomechanical_analysis.injury_risk_note}</p></article></div></section>}
        {report?.nutrition_recovery_analysis && <section className="agent-suite-section recovery-section"><div className="report-section-heading"><div><span className="eyebrow">SUPORTE À PERFORMANCE</span><h3>Preparação, hidratação e recuperação</h3></div><span className="guidance-pill">orientação geral</span></div><div className="agent-detail-grid"><article><small>Antes do treino</small><p>{report.nutrition_recovery_analysis.pre_workout_protocol}</p></article><article><small>Durante o treino</small><p>{report.nutrition_recovery_analysis.intra_hydration_strategy}</p></article><article><small>Após o esforço</small><p>{report.nutrition_recovery_analysis.post_recovery_recommendation}</p></article></div><p className="agent-safety-note">Recomendações gerais: individualização nutricional exige avaliação profissional, histórico clínico, duração do treino, clima e dados corporais.</p></section>}
        {report?.limitations?.length ? <div className="report-limitations"><strong>Limitações da análise</strong><p>{report.limitations.join(" · ")}</p></div> : null}
        <form className="report-feedback" onSubmit={(event) => void submitFeedback(event)}>
          <div><span className="eyebrow">SUA EXPERIÊNCIA</span><h3>Este relatório ajudou seu próximo treino?</h3><p>A avaliação melhora o produto; ela nunca altera sua nota ou o RPI.</p></div>
          <div className="feedback-controls">
            <div className="feedback-stars" role="radiogroup" aria-label="Nota do relatório">{[1, 2, 3, 4, 5].map((rating) => <button key={rating} type="button" role="radio" aria-checked={feedback.rating === rating} aria-label={`${rating} ${rating === 1 ? "estrela" : "estrelas"}`} className={feedback.rating >= rating ? "selected" : ""} onClick={() => setFeedback((current) => ({ ...current, rating }))}>★</button>)}</div>
            <div className="feedback-useful"><span>Foi útil?</span><button type="button" className={feedback.useful ? "selected" : ""} onClick={() => setFeedback((current) => ({ ...current, useful: true }))}>Sim</button><button type="button" className={!feedback.useful ? "selected" : ""} onClick={() => setFeedback((current) => ({ ...current, useful: false }))}>Ainda não</button></div>
            <label>Comentário opcional<textarea maxLength={1000} value={feedback.comment ?? ""} onChange={(event) => setFeedback((current) => ({ ...current, comment: event.target.value }))} placeholder="O que foi mais útil ou o que podemos tornar mais claro?" /></label>
            <button className="feedback-submit" disabled={feedbackBusy || !feedback.rating}>{feedbackBusy ? "Salvando…" : "Enviar avaliação"}</button>
            {feedbackNotice && <span className="feedback-notice" role="status">{feedbackNotice}</span>}
          </div>
        </form>
        <p className="report-disclaimer">Este relatório apoia o desenvolvimento esportivo e não substitui avaliação médica, fisioterapêutica ou presencial do treinador.</p>
      </>}
    </section>
  );
}

function BillingSummary({ plan, subscriptionStatus, credits }: { plan: string; subscriptionStatus: string | null; credits: number }) {
  const planName = ({ free: "Piloto", starter: "Starter", performance: "Performance", evolution: "Evolution", academy: "Academy" } as Record<string, string>)[plan] ?? plan;
  return (
    <section className="billing-summary">
      <div><span className="eyebrow">PLANO & CRÉDITOS</span><h2>{planName}</h2><p>Billing Stripe preparado para assinatura e análises avulsas.</p></div>
      <div className="billing-metrics"><article><small>Status</small><strong>{subscriptionStatus === "active" ? "Ativo" : subscriptionStatus ?? "Piloto"}</strong></article><article><small>Créditos avulsos</small><strong>{credits}</strong></article><span>Checkout permanece bloqueado até a ativação segura do catálogo.</span></div>
    </section>
  );
}

function Dashboard({ session, athlete }: { session: Session; athlete: Athlete }) {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [rpiSnapshots, setRpiSnapshots] = useState<RpiSnapshot[]>([]);
  const [plan, setPlan] = useState("free");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [credits, setCredits] = useState(0);
  const [selectedReport, setSelectedReport] = useState<Analysis | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const activeCount = analyses.filter((item) => !["completed", "failed_terminal", "rejected_input", "cancelled"].includes(item.status)).length;
  const completed = analyses.filter((item) => item.status === "completed");
  const latestRpi = rpiSnapshots[0] ?? null;

  const loadAnalyses = useCallback(async () => {
    const [analysisResult, rpiResult, tenantResult, subscriptionResult, ledgerResult] = await Promise.all([
      supabase.from("analyses").select("id,tenant_id,athlete_id,status,progress,stroke_type,video_context,created_at,completed_at,failure_code,retry_count,human_review_required,review_status,reports(id,published_at,executive_summary,limitations,agent_suite_version,athlete_metadata,head_coach_analysis,biomechanical_analysis,nutrition_recovery_analysis,elite_coach_analysis),scores(technique,biomechanics,footwork,consistency,power,control,tactics,mental,conditioning,general_rpi,stroke_rpi,confidence,coverage_ratio,reliability,uncertainty,created_at)").eq("athlete_id", athlete.id).order("created_at", { ascending: false }),
      supabase.from("athlete_rpi_snapshots").select("id,status,rpi,lower_bound,upper_bound,confidence,coverage,momentum,group_scores,missing_requirements,calculated_at").eq("athlete_id", athlete.id).order("calculated_at", { ascending: false }),
      supabase.from("tenants").select("plan").eq("id", athlete.tenant_id).maybeSingle(),
      supabase.from("billing_subscriptions").select("status").eq("tenant_id", athlete.tenant_id).order("updated_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("billing_credit_ledger").select("delta").eq("tenant_id", athlete.tenant_id),
    ]);
    const queryError = analysisResult.error ?? rpiResult.error ?? tenantResult.error ?? subscriptionResult.error ?? ledgerResult.error;
    if (queryError) setError(queryError.message);
    else {
      setAnalyses((analysisResult.data ?? []).map((analysis) => ({
        ...analysis,
        // PostgREST returns unique relations as an object and non-unique
        // relations as an array. Keep the view model stable in both cases.
        reports: relationArray(analysis.reports),
        scores: relationArray(analysis.scores),
      })) as Analysis[]);
      setRpiSnapshots((rpiResult.data ?? []) as RpiSnapshot[]);
      setPlan(tenantResult.data?.plan ?? "free");
      setSubscriptionStatus(subscriptionResult.data?.status ?? null);
      setCredits((ledgerResult.data ?? []).reduce((sum, entry) => sum + entry.delta, 0));
    }
  }, [athlete.id]);

  useEffect(() => { void loadAnalyses(); }, [loadAnalyses]);
  useEffect(() => {
    if (!activeCount) return;
    const timer = window.setInterval(() => void loadAnalyses(), 8_000);
    return () => window.clearInterval(timer);
  }, [activeCount, loadAnalyses]);

  async function retryAnalysis(analysisId: string) {
    setRetryingId(analysisId);
    setError("");
    try {
      await api(`/v1/analyses/${analysisId}/retry`, session, { method: "POST" });
      await loadAnalyses();
    } catch (retryError) {
      setError(messageFrom(retryError));
    } finally {
      setRetryingId(null);
    }
  }

  async function cancelAnalysis(analysisId: string) {
    if (!window.confirm("Cancelar esta análise e remover o vídeo enviado? Esta ação não pode ser desfeita.")) return;
    setCancellingId(analysisId);
    setError("");
    try {
      await api(`/v1/analyses/${analysisId}`, session, { method: "DELETE" });
      await loadAnalyses();
    } catch (cancelError) {
      setError(messageFrom(cancelError));
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div className="dashboard-shell">
      <aside className="sidebar">
        <Brand />
        <nav><a className="active">Visão geral</a><a>Minhas análises</a><a>Evolução</a><a>Meu perfil</a></nav>
        <div className="sidebar-foot"><span className="avatar">{athlete.full_name.slice(0, 1).toUpperCase()}</span><div><strong>{athlete.full_name}</strong><small>Plano {plan}</small></div></div>
      </aside>
      <main className="dashboard-main">
        <header className="topbar"><div><span className="eyebrow">PERFORMANCE CENTER</span><h1>Olá, {athlete.full_name.split(" ")[0]}.</h1></div><button className="ghost" onClick={() => void supabase.auth.signOut()}>Sair</button></header>
        {selectedReport ? <ReportViewer analysis={selectedReport} session={session} onClose={() => setSelectedReport(null)} /> : <>
        <section className="metrics">
          <article><small>Análises concluídas</small><strong>{completed.length}</strong><span>histórico validado</span></article>
          <article><small>Em processamento</small><strong>{activeCount}</strong><span>pipeline assíncrono</span></article>
          <article className="rpi"><small>RPI Global</small><strong>{latestRpi?.rpi?.toFixed(2) ?? (completed.length ? "Incompleto" : "Novo")}</strong><span>{latestRpi?.rpi != null ? `${latestRpi.status === "established" ? "estabelecido" : "provisório"} · confiança ${Math.round(latestRpi.confidence * 100)}%` : "complete os quatro pilares técnicos"}</span></article>
        </section>
        <PerformanceOverview analyses={analyses} rpiSnapshots={rpiSnapshots} />
        <BillingSummary plan={plan} subscriptionStatus={subscriptionStatus} credits={credits} />
        <div className="dashboard-grid">
          <UploadPanel athlete={athlete} session={session} onCreated={loadAnalyses} />
          <section className="history-card">
            <div className="card-heading"><div><span className="eyebrow">HISTÓRICO</span><h2>Suas análises</h2></div><span className="live-dot">ao vivo</span></div>
            {error && <div className="error-box">{error}</div>}
            {!analyses.length ? <div className="empty"><strong>Seu primeiro marco começa aqui.</strong><p>Envie um vídeo para inaugurar sua linha de evolução.</p></div> : (
              <div className="analysis-list">{analyses.map((item) => (
                <article key={item.id}>
                  <div className="stroke-icon">{item.stroke_type === "serve" ? "S" : "R"}</div>
                  <div className="analysis-copy"><strong>{strokeLabel(item.stroke_type)}</strong><small>{new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(new Date(item.created_at))} · {statusLabels[item.status] ?? item.status}</small><div className="mini-progress"><i style={{ width: `${item.progress}%` }} /></div></div>
                  <div className="analysis-actions">{item.reports?.some((report) => report.published_at) ? <button className="report-link" type="button" onClick={() => setSelectedReport(item)}>Abrir relatório</button> : ["failed_retryable", "failed_terminal"].includes(item.status) && item.retry_count < 3 ? <button className="report-link" type="button" disabled={retryingId === item.id} onClick={() => void retryAnalysis(item.id)}>{retryingId === item.id ? "Reiniciando…" : "Tentar novamente"}</button> : <b>{item.progress}%</b>}{["created", "uploaded", "validating", "preprocessing", "analyzing_context", "analyzing_specialists", "validating_claims", "rendering", "failed_retryable"].includes(item.status) && <button className="cancel-analysis" type="button" aria-label="Cancelar esta análise" title="Cancelar esta análise" disabled={cancellingId === item.id} onClick={() => void cancelAnalysis(item.id)}>{cancellingId === item.id ? "…" : "×"}</button>}</div>
                </article>
              ))}</div>
            )}
          </section>
        </div>
        </>}
      </main>
    </div>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [athlete, setAthlete] = useState<Athlete | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const adminRoute = new URLSearchParams(window.location.search).get("admin") === "1";

  const loadAthlete = useCallback(async () => {
    const { data } = await supabase.from("athletes").select("id,tenant_id,full_name,level,dominant_hand").order("created_at").limit(1).maybeSingle();
    setAthlete(data as Athlete | null); setLoading(false);
  }, []);

  const refreshSession = useCallback(async () => {
    const { data } = await supabase.auth.refreshSession();
    setSession(data.session);
  }, []);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => { setSession(data.session); if (data.session) void loadAthlete(); else setLoading(false); });
    const { data: listener } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
      setSession(next);
      if (next) void loadAthlete(); else { setAthlete(null); setLoading(false); }
    });
    return () => listener.subscription.unsubscribe();
  }, [loadAthlete]);

  const content = useMemo(() => {
    if (loading) return <div className="splash"><Brand /><span>Preparando seu Performance Center…</span></div>;
    if (!session) return <AuthScreen />;
    if (session.user.email && (passwordRecovery || (session.user.user_metadata.password_set !== true && session.user.app_metadata.provider !== "google"))) {
      return <PasswordSetup email={session.user.email} onDone={async () => { await refreshSession(); setPasswordRecovery(false); }} />;
    }
    if (adminRoute) return <AdminDashboard session={session} />;
    if (!athlete) return <Onboarding session={session} onDone={loadAthlete} />;
    return <Dashboard session={session} athlete={athlete} />;
  }, [adminRoute, athlete, loading, loadAthlete, passwordRecovery, refreshSession, session]);

  return content;
}
